import type { SerializedError } from '@reduxjs/toolkit';

import axios from 'axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import {
  PLACEHOLDER_ACTIVITY_DATE_ISO,
  PLACEHOLDER_ACTIVITY_END_TIME,
  PLACEHOLDER_ACTIVITY_START_TIME,
} from 'src/constants/activity';

import initialConfig from '../initialConfig';

const initialState: {
  anonymous_listingListWithActivitiesAndBlogListData: any;
  anonymous_listingListWithActivitiesAndBlogListLoading: boolean;
  anonymous_listingListWithActivitiesAndBlogListError: SerializedError | null;
  page: number;
  // En son gönderilen isteğin requestId'si; stale response'ları atmak için kullanılır.
  currentRequestId: string | null;
} = {
  anonymous_listingListWithActivitiesAndBlogListData: {},
  anonymous_listingListWithActivitiesAndBlogListLoading: false,
  anonymous_listingListWithActivitiesAndBlogListError: null,
  page: 1,
  currentRequestId: null,
};

export interface IAnonymousListingListWithActivitiesAndBlogListFilters {
  sort?: string;
  listingSortings?: string;
  fee: string | null;
  plus?: boolean | null;
  fromQuota: number | null;
  toQuota: number | null;
  byCoordinates: boolean | null;
  long: number | null;
  lat: number | null;
  finished: boolean | null;
  page: number | null;
  pageSize: number | null;
  activityBlogFilterAdditionalDetails?: string | null;
  listing: {
    title: string | null;
    ageInterval: number[] | null;
    format: string | null;
    category: string | null;
  };
  location: {
    district: string | null;
    city: string | null;
    parking: string | null;
  };
  schedule: {
    days: number[] | null;
    dateInterval: {
      from: string | null;
      to: string | null;
    };
  };
  price: {
    from: number | null;
    to: number | null;
  };
}

const transformListingsToActivities = (responseData: any) => {
  if (!responseData) {
    return { count: 0, data: [], listings: [] };
  }

  const listings = responseData?.data ?? [];
  const activities = listings.map((listing: any) => {
    if (!listing) {
      return null;
    }

    const { activities: listingActivities = [], ...listingRest } = listing;

    if (!listingActivities || listingActivities.length === 0) {
      return null;
    }

    // Use first activity as main display
    const firstActivity = listingActivities[0];
    const hasMultipleActivities = listingActivities.length > 1;

    // Check for multiple locations (by location._id)
    const uniqueLocationIds = new Set(
      listingActivities
        .map((act: any) => act?.location?._id)
        .filter((id: any) => id != null)
    );
    const hasMultipleLocations = uniqueLocationIds.size > 1;

    // Check for multiple dates (by schedule.startDate, excluding placeholders)
    const uniqueStartDates = new Set(
      listingActivities
        .map((act: any) => act?.schedule?.startDate)
        .filter(
          (date: any) => date != null && date !== PLACEHOLDER_ACTIVITY_DATE_ISO
        )
    );
    const hasMultipleDates = uniqueStartDates.size > 1;

    // Check for multiple times (by schedule.days[0].startTime and endTime, excluding placeholders)
    const uniqueTimes = new Set(
      listingActivities
        .map((act: any) => {
          const firstDay = act?.schedule?.days?.[0];
          if (!firstDay) return null;
          const startTime = firstDay.startTime;
          const endTime = firstDay.endTime;
          // Exclude placeholder times
          if (
            startTime === PLACEHOLDER_ACTIVITY_START_TIME ||
            endTime === PLACEHOLDER_ACTIVITY_END_TIME
          ) {
            return null;
          }
          return `${startTime}-${endTime}`;
        })
        .filter((time: any) => time != null)
    );
    const hasMultipleTimes = uniqueTimes.size > 1;

    return {
      ...firstActivity,
      listing: {
        ...listingRest,
        ...(firstActivity?.listing ?? {}),
        exteriorOrganizer:
          listingRest?.exteriorOrganizer ?? firstActivity?.listing?.exteriorOrganizer ?? null,
      },
      // Add metadata for multi-activity handling
      hasMultipleActivities,
      totalActivityCount: listingActivities.length,
      hasMultipleLocations,
      hasMultipleDates,
      hasMultipleTimes,
      allActivities: listingActivities.map((activity: any) => ({
        ...activity,
        listing: {
          ...listingRest,
          ...(activity?.listing ?? {}),
          exteriorOrganizer:
            listingRest?.exteriorOrganizer ?? activity?.listing?.exteriorOrganizer ?? null,
        },
      })),
    };
  }).filter((activity: any) => activity !== null);

  return {
    count: responseData?.count ?? activities.length,
    data: activities,
    listings,
  };
};

export const anonymous_listingListWithActivitiesAndBlogListFunc = createAsyncThunk(
  'anonymous_listingListWithActivitiesAndBlogList/anonymous_listingListWithActivitiesAndBlogListFunc',
  async (values: IAnonymousListingListWithActivitiesAndBlogListFilters) => {
    const data1 = JSON.stringify({
      query: `query Anonymous_listingListWithActivitiesAndBlogList($activityFilter: FilterActivitiesByAnonymousInput, $activityBlogFilter: FilterActivityBlogsByAnonymousInput, $listingSortings: ListingSortingForListingListWithActivities, $pagination: Pagination) {
  anonymous_listingListWithActivitiesAndBlogList(activityFilter: $activityFilter, activityBlogFilter: $activityBlogFilter, listingSortings: $listingSortings, pagination: $pagination) {
    count
    data {
      _id
      activities {
        _id
        title
        listing {
          _id
          title
          category
          subCategory
          ageInterval
          format
          description
          images
          coverImage
          companionship
          rating {
            average
            numberOfReviews
            createdAt
            updatedAt
          }
          status
          rejectionDetail
        }
        schedule {
          _id
          repeatedIntervalType
          startDate
          endDate
          totalDayCount
          days {
            _id
            activity {
              _id
            }
            activityBlog {
              _id
            }
            date
            day
            startTime
            endTime
            createdAt
            updatedAt
          }
          openHour
          closeHour
          createdAt
          updatedAt
        }
        location {
          _id
          city
          district
          country
          address {
            title
            description
          }
          lat
          long
          parking
          status
          rejectionDetail
        }
        additionalDetails
        quota
        hoursBeforeReservationExpires
        fee
        isReservationRequired
        externalLink
        externalMail
        externalPhoneNumber
        price
        duration
        status
        participantCount
        priority
        createdBy {
          _id
          displayName
          displayImageUrl
          description
          role
        }
        updatedBy {
          _id
          displayName
          displayImageUrl
          description
          role
        }
        createdAt
        updatedAt
        plus
        isBlog
      }
    }
  }
}`,
      variables: {
        activityFilter: {
          additionalDetails: null,
          duration: {
            from: null,
            to: null,
          },
          fee: values?.fee ?? null,
          listing: {
            ageInterval: values?.listing?.ageInterval ?? null,
            category: values?.listing?.category ?? null,
            companionship: null,
            description: null,
            format: values?.listing?.format ?? null,
            title: values?.listing?.title ?? null,
          },
          price: {
            from: values?.price?.from ?? null,
            to: values?.price?.to ?? null,
          },
          quota: {
            from: values?.fromQuota ?? null,
            to: values?.toQuota ?? null,
          },
          location: {
            city: values?.location?.city ?? null,
            district: values?.location?.district ?? null,
            parking: values?.location?.parking ?? null,
            byCoordinates:
              values?.byCoordinates === false
                ? null
                : {
                    radius: null,
                    long: values?.long ?? null,
                    lat: values?.lat ?? null,
                  },
          },
          schedule: {
            finished: false,
            days: values?.schedule?.days ?? null,
            dateInterval: {
              from: values?.schedule?.dateInterval?.from ?? null,
              to: values?.schedule?.dateInterval?.to ?? null,
            },
          },
        },
        activityBlogFilter: {
          additionalDetails: values?.activityBlogFilterAdditionalDetails ?? null,
          duration: {
            from: null,
            to: null,
          },
          fee: values?.fee ?? null,
          plus: values?.plus ?? null,
          listing: {
            ageInterval: values?.listing?.ageInterval ?? null,
            category: values?.listing?.category ?? null,
            companionship: null,
            description: null,
            format: values?.listing?.format ?? null,
            title: values?.listing?.title ?? null,
          },
          location: {
            city: values?.location?.city ?? null,
            district: values?.location?.district ?? null,
            parking: values?.location?.parking ?? null,
            byCoordinates:
              values?.byCoordinates === false
                ? null
                : {
                    radius: null,
                    long: values?.long ?? null,
                    lat: values?.lat ?? null,
                  },
          },
          schedule: values?.schedule?.days || values?.schedule?.dateInterval?.from || values?.schedule?.dateInterval?.to
            ? {
                finished: false,
                days: values?.schedule?.days ?? null,
                dateInterval: {
                  from: values?.schedule?.dateInterval?.from ?? null,
                  to: values?.schedule?.dateInterval?.to ?? null,
                },
              }
            : null,
        },
        pagination: {
          page: values.page,
          pageSize: values.pageSize,
        },
        listingSortings: values?.listingSortings ?? null,
      },
    });

    const config = initialConfig(process.env.REACT_APP_BASE_URL_ACTIVITY || '', data1, '');

    const response = axios(config)
      .then((res) => {
        const data = res.data;
        return data;
      })
      .catch((err) => err);

    const data = await response;
    return data;
  }
);

const anonymous_listingListWithActivitiesAndBlogList = createSlice({
  name: 'anonymous_listingListWithActivitiesAndBlogList',
  initialState,
  reducers: {
    setAnonymousListingListPagination: (state, action) => {
      state.page = action.payload;
    },
    resetAnonymousListingListState: (state) => {
      state.page = 1;
      state.anonymous_listingListWithActivitiesAndBlogListData = {};
      state.anonymous_listingListWithActivitiesAndBlogListLoading = false;
      state.anonymous_listingListWithActivitiesAndBlogListError = null;
      state.currentRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(anonymous_listingListWithActivitiesAndBlogListFunc.pending, (state, action) => {
      state.anonymous_listingListWithActivitiesAndBlogListLoading = true;
      state.currentRequestId = action.meta.requestId;
    });
    builder.addCase(anonymous_listingListWithActivitiesAndBlogListFunc.fulfilled, (state, action) => {
      // Sadece en güncel isteğin cevabını işle; eski/stale response'ları sessizce yok say.
      if (state.currentRequestId && state.currentRequestId !== action.meta.requestId) {
        return;
      }
      state.anonymous_listingListWithActivitiesAndBlogListLoading = false;
      const listingsResponse =
        action.payload?.data?.anonymous_listingListWithActivitiesAndBlogList;
      state.anonymous_listingListWithActivitiesAndBlogListData =
        transformListingsToActivities(listingsResponse);
      state.anonymous_listingListWithActivitiesAndBlogListError =
        action.payload.errors || action.payload?.response?.status;
    });
    builder.addCase(anonymous_listingListWithActivitiesAndBlogListFunc.rejected, (state, action) => {
      if (state.currentRequestId && state.currentRequestId !== action.meta.requestId) {
        return;
      }
      state.anonymous_listingListWithActivitiesAndBlogListLoading = false;
      state.anonymous_listingListWithActivitiesAndBlogListError = action.error;
    });
  },
});

export const {
  setAnonymousListingListPagination,
  resetAnonymousListingListState,
} = anonymous_listingListWithActivitiesAndBlogList.actions;
export default anonymous_listingListWithActivitiesAndBlogList.reducer;
